/**
 * Phase 8 T-06b §20.C / C-41 · Unified Assistant thread component.
 *
 * One component, three surfaces (Smart Match middle column, topbar slide-over,
 * record detail rail tab). State lives in `useAgentThread` — the optional
 * `runPrompt` prop lets a surface plug in a bespoke one-shot runner (Smart
 * Match's tool-aware pipeline); otherwise the hook drives the real streaming
 * runner through `agentRunnerService` (C-41).
 *
 * Anatomy (per §20.C):
 *   ┌ model header ─── `GGJ Reasoning v2` + 🔒 private to you
 *   ├ turn list ────── user right / assistant left (emerald gutter icon)
 *   │                   each assistant turn has a collapsible reasoning trace,
 *   │                   inline "🔧 Using <tool>…" pills during streaming,
 *   │                   tool-result cards once tools return, and 2–4 chips
 *   └ composer ─────── textarea, scope pill, /commands popup, Send
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock, Sparkles, Wrench } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  useAgentThread,
  type AgentActionChip,
  type AgentScope,
  type AgentTurn,
} from '../hooks/useAgentThread';
import { useAgentRouteContext } from '../hooks/useAgentRouteContext';
import { AgentModelChip } from './AgentModelChip';
import { AgentReasoningTrace } from './AgentReasoningTrace';
import { AgentActionChips } from './AgentActionChips';
import { AgentComposer, type AgentComposerHandle, type SlashCommandId } from './AgentComposer';
import { AgentQuickActions } from './AgentQuickActions';
import { AgentSourcingResults } from './AgentSourcingResults';
import { AgentPlanCard } from './AgentPlanCard';
import {
  AgentAutonomousToggle,
  useAgentAutonomousMode,
} from './AgentAutonomousToggle';
import { SimpleMarkdown } from './SimpleMarkdown';
import type { ScopeId } from './AgentScopePill';

const EMPTY_SUGGESTIONS_BY_KIND: Record<string, string[]> = {
  candidate: [
    'Summarise this candidate',
    'Draft an outreach email',
    "What's blocking this?",
    'Compare to top performer',
  ],
  application: [
    'Why did we reject this applicant?',
    'Summarise the hiring decision',
    'Draft a follow-up email',
    'Compare to similar applications',
  ],
  job: [
    'Who are the best matches?',
    'Summarise open criteria',
    'Draft a sourcing brief',
    'Compare to similar roles',
  ],
  client: [
    'Summarise recent activity',
    'Draft a check-in email',
    'Which roles are open?',
    'What blockers remain?',
  ],
  'smart-match': [
    'Fetch more candidates',
    'Update search',
    'Update evals',
    'Filter candidates',
  ],
  page: ['Help me with this page', 'Summarise what I see', 'Suggest next steps', 'Draft an update'],
  record: ['Summarise this record', 'Draft a message', "What's the status?", 'Next best action'],
};

interface Props {
  scope: AgentScope;
  scopeId: string;
  entityKind?: string;
  entityId?: string;
  /**
   * Legacy one-shot runner override — when provided, the hook awaits its
   * result instead of streaming via `agentRunnerService`. Smart Match's
   * `AgentThreadColumn` uses this to keep its tool-aware pipeline.
   */
  runPrompt?: (userText: string) => Promise<{
    content: string;
    reasoning?: { thoughtMs: number; steps: { name: string; durationMs: number }[] };
    chips?: AgentActionChip[];
  }>;
  /** Composer auto-focus. */
  autoFocusComposer?: boolean;
  /** Hide the model header (used inside the Smart Match column which already
   *  has a column header). */
  showModelHeader?: boolean;
  /** Optional imperative handle exposing the composer focus function. */
  composerHandleRef?: React.MutableRefObject<AgentComposerHandle | null>;
  /**
   * Whitelist of tools the streaming runner may call. Surface-specific —
   * record detail passes `['data.candidates.list','data.jobs.list']` so the
   * rail cannot spend budget on external sourcing.
   */
  tools?: string[];
  /**
   * Phase 8 · C-48 — Render the route-scoped quick action chip row above
   * the composer and inject the same route context into every outgoing
   * turn. Default `true`; set to false for surfaces that don't want the
   * extra chrome (e.g. the topbar slide-over when it isn't tied to a
   * record page).
   */
  enableQuickActions?: boolean;
  /**
   * Callback fired when sourcing tool results arrive (source.* tools).
   * Used by Smart Match canvas to surface external sourcing results as match cards.
   */
  onSourcingResults?: (results: import('../hooks/useAgentThread').AgentToolResult[]) => void;
}

export function AgentThread({
  scope,
  scopeId,
  entityKind,
  entityId,
  runPrompt,
  autoFocusComposer = false,
  showModelHeader = true,
  composerHandleRef,
  tools,
  enableQuickActions = true,
  onSourcingResults,
}: Props) {
  const { user } = useAuth();
  const firstName = useMemo(() => {
    const raw = user?.fullName?.split(' ')[0] || user?.email?.split('@')[0] || 'there';
    return raw;
  }, [user]);

  // Phase 8 · C-48 — Resolve the current route context so we can show the
  // chip row above the composer AND inject the block into every turn.
  // Surfaces that don't want the chips (`enableQuickActions=false`) still
  // benefit from context injection because the hook runs unconditionally.
  const routeContext = useAgentRouteContext();

  // Phase 8 · C-48 — autonomous mode is a per-user preference. Stored
  // in localStorage today; a follow-up will back-fill
  // `ggj_b2b_org_users.prefs.agent_autonomous` on write.
  const [autonomousMode, setAutonomousMode] = useAgentAutonomousMode();

  const { turns, thinking, send, approvePlan, rejectPlan, cancelPlan } = useAgentThread({
    scope,
    scopeId,
    entityKind,
    entityId,
    runPrompt,
    tools,
    routeContext,
    autonomousMode,
    onSourcingResults,
  });

  const [composerScope, setComposerScope] = useScopePersisted(`ggj.assistant.scope.${scope}`);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const internalComposerRef = useRef<AgentComposerHandle | null>(null);
  const composerRefToUse = composerHandleRef ?? internalComposerRef;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, thinking]);

  const onSend = useCallback(
    (text: string, _commands: SlashCommandId[]) => {
      void send(text);
    },
    [send],
  );

  const entityLabel = entityKind
    ? entityKind
    : scope === 'smart-match'
      ? 'search'
      : scope === 'record'
        ? 'record'
        : 'page';

  const suggestionKey = entityKind && EMPTY_SUGGESTIONS_BY_KIND[entityKind] ? entityKind : scope;
  const suggestions: AgentActionChip[] = useMemo(
    () =>
      (EMPTY_SUGGESTIONS_BY_KIND[suggestionKey] ?? EMPTY_SUGGESTIONS_BY_KIND.page ?? []).map(
        (label, i) => ({ id: `suggest-${i}`, label }),
      ),
    [suggestionKey],
  );

  const handleEmptyChip = useCallback(
    (chip: AgentActionChip) => {
      void send(chip.label);
    },
    [send],
  );

  // Hook for T-06c: the intake surface belongs to `scope="smart-match"` with
  // no scopeId. Render nothing here and let T-06c fill in the intake UI.
  if (scope === 'smart-match' && !scopeId) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showModelHeader ? (
        <div
          className="shrink-0 flex h-8 items-center justify-between border-b px-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <AgentModelChip />
          <span
            className="inline-flex items-center gap-1 text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            title="Only you see this thread"
          >
            <Lock className="h-2.5 w-2.5" aria-hidden />
            private to you
          </span>
        </div>
      ) : null}

      <div
        className="ggj-scroll-thin min-h-0 flex-1 overflow-y-scroll px-3 py-3"
        role="log"
        aria-live="polite"
        aria-busy={thinking}
      >
        {turns.length === 0 ? (
          <AgentEmptyState
            firstName={firstName}
            entityLabel={entityLabel}
            chips={suggestions}
            onChipClick={handleEmptyChip}
          />
        ) : (
          <ol className="flex flex-col gap-4">
            {turns.map((t) => (
              <AgentTurnRow
                key={t.id}
                turn={t}
                onApprovePlan={approvePlan}
                onRejectPlan={rejectPlan}
                onCancelPlan={cancelPlan}
              />
            ))}
            <div ref={bottomRef} />
          </ol>
        )}
      </div>

      {enableQuickActions ? (
        <AgentQuickActions
          ctx={routeContext}
          onPickAction={(prompt) => {
            composerRefToUse.current?.setText(prompt);
          }}
        />
      ) : null}

      <AgentComposer
        ref={composerRefToUse}
        scope={composerScope}
        onScopeChange={setComposerScope}
        onSend={onSend}
        thinking={thinking}
        autoFocus={autoFocusComposer}
        footerRight={
          <AgentAutonomousToggle value={autonomousMode} onChange={setAutonomousMode} compact />
        }
      />
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────

function AgentEmptyState({
  firstName,
  entityLabel,
  chips,
  onChipClick,
}: {
  firstName: string;
  entityLabel: string;
  chips: AgentActionChip[];
  onChipClick: (chip: AgentActionChip) => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--ggj-accent-soft,#D1FAE5)' }}
      >
        <Sparkles className="h-5 w-5" style={{ color: '#065F46' }} aria-hidden />
      </div>
      <p className="text-[14px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
        Hey {firstName}, what can I help with on this {entityLabel}?
      </p>
      {chips.length ? <AgentActionChips chips={chips} onChipClick={onChipClick} /> : null}
    </div>
  );
}

// ── Individual turn ───────────────────────────────────────────────────────

function AgentTurnRow({
  turn,
  onApprovePlan,
  onRejectPlan,
  onCancelPlan,
}: {
  turn: AgentTurn;
  onApprovePlan: (planId: string, mode: 'all' | 'stepwise') => void;
  onRejectPlan: (planId: string) => void;
  onCancelPlan: (planId: string) => void;
}) {
  if (turn.role === 'user') {
    return (
      <li className="flex justify-end">
        <div
          className="max-w-[560px] whitespace-pre-wrap rounded-[10px] px-3 py-2 text-[13px] leading-[1.5]"
          style={{
            background: 'var(--vscode-bg-hover)',
            color: 'var(--vscode-text-primary)',
          }}
        >
          {turn.content}
        </div>
      </li>
    );
  }

  return (
    <li className="flex gap-2.5">
      <div
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
        aria-hidden
      >
        <Sparkles className="h-3 w-3" />
      </div>
      <div
        className={`min-w-0 flex-1 ${turn.streaming ? 'border-l-2 pl-2 animate-pulse' : ''}`}
        style={turn.streaming ? { borderColor: 'var(--ggj-accent-soft,#D1FAE5)' } : undefined}
      >
        {turn.reasoning ? (
          <AgentReasoningTrace
            thoughtMs={turn.reasoning.thoughtMs}
            steps={turn.reasoning.steps}
          />
        ) : null}
        <div
          className="text-[13px] leading-[1.5]"
          style={{ color: 'var(--vscode-text-primary)' }}
        >
          {turn.streaming && !turn.content ? (
            <span
              className="inline-flex items-center gap-1"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-current" />
              <span
                className="inline-block h-1 w-1 animate-pulse rounded-full bg-current"
                style={{ animationDelay: '120ms' }}
              />
              <span
                className="inline-block h-1 w-1 animate-pulse rounded-full bg-current"
                style={{ animationDelay: '240ms' }}
              />
            </span>
          ) : (
            <SimpleMarkdown content={turn.content} />
          )}
        </div>
        {turn.toolCalls?.length ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {turn.toolCalls.map((call) => (
              <span
                key={call.id}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
                style={{
                  background: 'var(--vscode-bg-hover)',
                  color: 'var(--vscode-text-secondary)',
                }}
                title={call.argsJson}
              >
                <Wrench className="h-2.5 w-2.5" aria-hidden />
                {call.done ? `Used ${call.name}` : `Using ${call.name}…`}
              </span>
            ))}
          </div>
        ) : null}
        {turn.toolResults?.length ? (
          <div className="mt-2 flex flex-col gap-1.5">
            {turn.toolResults.map((r) =>
              // C-44: `source.*` tools render an interactive candidate
              // list with "Save to pool" CTAs when structured payload is
              // available (live streaming turn). Server-replayed turns
              // fall back to the compact summary card below.
              r.name.startsWith('source.') && r.data ? (
                <AgentSourcingResults
                  key={r.id}
                  toolName={r.name}
                  ok={r.ok}
                  summary={r.summary}
                  data={r.data}
                />
              ) : (
                <div
                  key={r.id}
                  className="rounded-[8px] border px-2.5 py-1.5 text-[12px]"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    background: 'var(--vscode-bg-hover)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  <div
                    className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: r.ok ? '#065F46' : '#9F1239' }}
                  >
                    <Wrench className="h-2.5 w-2.5" aria-hidden />
                    {r.name}
                  </div>
                  <div className="whitespace-pre-wrap">{r.summary}</div>
                </div>
              ),
            )}
          </div>
        ) : null}
        {turn.chips?.length ? <AgentActionChips chips={turn.chips} /> : null}
        {turn.plan ? (
          <AgentPlanCard
            plan={turn.plan}
            onApprove={(planId, mode) => void onApprovePlan(planId, mode)}
            onReject={(planId) => void onRejectPlan(planId)}
            onCancel={(planId) => void onCancelPlan(planId)}
          />
        ) : null}
      </div>
    </li>
  );
}

// ── Scope persistence ─────────────────────────────────────────────────────

function useScopePersisted(storageKey: string): [ScopeId, (next: ScopeId) => void] {
  const [scope, setScope] = useState<ScopeId>(() => {
    if (typeof window === 'undefined') return 'org';
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'org' || raw === 'career' || raw === 'external') return raw;
    } catch {
      /* ignore */
    }
    return 'org';
  });
  const set = useCallback(
    (next: ScopeId) => {
      setScope(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );
  return [scope, set];
}
